import * as React from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  ClipboardCheck,
  Filter,
  MessageSquarePlus,
  Search,
  ServerCog,
  SquareActivity,
  UserRoundCheck,
} from 'lucide-react'
import {
  useBosunCockpit,
  useBosunKanbanSummary,
  useBosunProjects,
  useBosunPortfolioActionItems,
  useCreateBosunIntakeRequest,
  useWorkspaces,
  type BosunCockpitProjectSummaryRead,
  type BosunKanbanSummaryRead,
  type BosunProjectRead,
  type BosunUserActionItemRead,
  type WorkspaceRead,
} from '@/api/queries'
import ErrorFallback from '@/components/ErrorFallback'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Input, Textarea } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { PageHeader, SectionHeader } from '@/components/ui/page-header'
import { formatError } from '@/lib/format-error'
import { canManageMembers } from '@/api/workspace-permissions'

export const Route = createFileRoute('/_authenticated/projects/')({
  component: ProjectsIndexPage,
  errorComponent: ErrorFallback,
})

function statusTone(status: string): React.ComponentProps<typeof Badge>['tone'] {
  if (status === 'ready' || status === 'dry_run') return 'success'
  if (status === 'blocked' || status === 'failed') return 'danger'
  if (status === 'running' || status === 'queued') return 'info'
  return 'neutral'
}

function archiveTone(status: string): React.ComponentProps<typeof Badge>['tone'] {
  if (status === 'active') return 'success'
  if (status === 'archived') return 'warning'
  if (status === 'failed') return 'danger'
  if (status === 'archiving' || status === 'restoring') return 'info'
  return 'neutral'
}

function deletionTone(status: string): React.ComponentProps<typeof Badge>['tone'] {
  if (status === 'deleted') return 'success'
  if (status === 'deleting') return 'info'
  if (status === 'delete_failed') return 'danger'
  return 'neutral'
}

type ProjectFilter = 'all' | 'ready' | 'running' | 'blocked' | 'waiting' | 'archived'

const FILTERS: Array<{ value: ProjectFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'ready', label: 'Ready' },
  { value: 'running', label: 'In progress' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'waiting', label: 'Waiting' },
  { value: 'archived', label: 'Archived' },
]

function projectHealth(project: BosunProjectRead): ProjectFilter {
  if (project.archive_status !== 'active') return 'archived'
  if (project.status === 'blocked' || project.status === 'failed' || project.blocked_reason) {
    return 'blocked'
  }
  if (
    project.provider_steps.some(
      (step) => step.status === 'blocked' || step.status === 'manual_pending',
    )
  ) {
    return 'waiting'
  }
  if (project.status === 'ready' || project.status === 'dry_run') return 'ready'
  if (project.status === 'running' || project.status === 'queued') return 'running'
  return 'all'
}

function projectMatches(
  project: BosunProjectRead,
  workspace: WorkspaceRead,
  query: string,
  filter: ProjectFilter,
) {
  const health = projectHealth(project)
  if (filter !== 'all' && health !== filter) return false
  const haystack = [
    project.name,
    project.slug,
    project.description,
    project.status,
    project.app_hostname,
    project.hermes_instance_slug,
    project.kanban_board_slug,
    workspace.name,
    health,
    ...project.provider_steps.flatMap((step) => [
      step.label,
      step.status,
      step.todo ?? '',
      step.detail ?? '',
    ]),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
  return haystack.includes(query.trim().toLowerCase())
}

function projectProgress(project: BosunProjectRead, summary?: BosunKanbanSummaryRead): number {
  let score = 0
  const total = 6
  if (project.status !== 'draft') score += 1
  if (project.provider_steps.some((step) => step.status === 'completed' || step.status === 'ready')) {
    score += 1
  }
  if (project.hermes_instance_slug || project.status === 'ready') score += 1
  if (project.kanban_board_slug || summary) score += 1
  if ((summary?.active_tasks.length ?? 0) > 0 || (summary?.running_tasks.length ?? 0) > 0) {
    score += 1
  }
  if (project.status === 'ready') score += 1
  return Math.round((score / total) * 100)
}

function healthLine(project: BosunProjectRead): string {
  const failed = project.provider_steps.find((step) => step.error || step.status === 'failed')
  if (failed) return `${failed.label}: action required`
  const waiting = project.provider_steps.find(
    (step) => step.status === 'blocked' || step.status === 'manual_pending',
  )
  if (waiting) return `${waiting.label}: waiting on user`
  const running = project.provider_steps.find(
    (step) => step.status === 'in_progress' || step.status === 'running',
  )
  if (running) return `${running.label}: in progress`
  if (project.status === 'ready') return 'Launch stack ready'
  return 'Provisioning timeline available'
}

function ProjectCard({ workspaceUuid, project }: { workspaceUuid: string; project: BosunProjectRead }) {
  const { data: summary, isLoading: summaryLoading, error: summaryError } = useBosunKanbanSummary(
    workspaceUuid,
    project.uuid,
  )
  const health = projectHealth(project)
  const progress = projectProgress(project, summary)
  const waitingTask = summary?.waiting_tasks[0]
  const latestAction =
    summary?.waiting_tasks[0] ??
    summary?.blocked_tasks[0] ??
    summary?.running_tasks[0] ??
    summary?.active_tasks[0]
  const blockedCount = summary?.blocked_tasks.length ?? 0
  const runningCount = summary?.running_tasks.length ?? 0

  return (
    <Card interactive>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="truncate">
              <Link
                to="/projects/$workspaceUuid/$projectUuid"
                params={{ workspaceUuid, projectUuid: project.uuid }}
                className="rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
                aria-label={`Open cockpit for ${project.name}`}
              >
                {project.name}
              </Link>
            </CardTitle>
            <p className="mt-1 truncate text-sm text-muted-foreground">
              {project.app_hostname ?? project.slug}
            </p>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Badge tone={statusTone(project.status)}>{project.status}</Badge>
            {project.archive_status !== 'active' ? (
              <Badge tone={archiveTone(project.archive_status)}>{project.archive_status}</Badge>
            ) : null}
            {project.deletion_status !== 'active' ? (
              <Badge tone={deletionTone(project.deletion_status)}>
                {project.deletion_status}
              </Badge>
            ) : null}
          </div>
        </div>
      </CardHeader>
      <CardContent className="grid gap-3 text-sm">
        <div className="space-y-2 rounded-lg border border-white/10 bg-white/[0.035] p-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-muted-foreground">Launch progress</p>
            <p className="font-medium">{progress}%</p>
          </div>
          <Progress
            percent={progress}
            tone={project.status === 'ready' ? 'success' : blockedCount ? 'warning' : 'primary'}
          />
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="rounded-lg border border-white/10 bg-white/[0.035] p-3">
            <p className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">Health</span>
              <Badge
                tone={
                  health === 'blocked'
                    ? 'danger'
                    : health === 'waiting'
                      ? 'warning'
                      : statusTone(project.status)
                }
              >
                {health === 'all' ? project.status : health}
              </Badge>
            </p>
            <p className="mt-1 truncate font-medium">{healthLine(project)}</p>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/[0.035] p-3">
            <p className="text-muted-foreground">App</p>
            <p className="truncate font-medium">{project.app_hostname ?? 'Pending'}</p>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/[0.035] p-3">
            <p className="text-muted-foreground">Hermes</p>
            <p className="truncate font-medium">{project.hermes_instance_slug ?? 'Pending'}</p>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/[0.035] p-3 text-sm text-muted-foreground">
            {project.archive_status === 'active'
              ? project.deletion_status === 'active'
                ? 'Open the cockpit for PM activity, launch status, and release evidence.'
                : 'Cleanup is tracked on the project status page with resource-level results.'
              : 'Runtime archived; persistent project data is retained.'}
          </div>
        </div>
        {summaryLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : summaryError || !summary ? (
          <div className="rounded-lg border border-white/10 bg-white/[0.035] p-3 text-sm text-muted-foreground">
            Kanban snapshot pending
          </div>
        ) : (
          <div className="rounded-lg border border-white/10 bg-white/[0.035] p-3 text-sm">
            <div className="flex flex-wrap gap-2">
              <Badge tone="warning">Waiting {summary.counts.waiting}</Badge>
              <Badge tone="danger">Blocked {summary.counts.blocked}</Badge>
              <Badge tone="info">Open {summary.counts.in_progress + summary.counts.todo}</Badge>
              <Badge tone="success">Done {summary.counts.done}</Badge>
            </div>
            <p className="mt-2 text-muted-foreground">
              {latestAction ? `Latest action: ${latestAction.title}` : 'No open PM or user action items.'}
            </p>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Badge tone={project.hermes_instance_slug ? 'success' : 'neutral'}>
            Hermes {project.hermes_instance_slug ? 'online' : 'pending'}
          </Badge>
          <Badge tone={project.kanban_board_slug ? 'info' : 'neutral'}>
            Kanban {project.kanban_board_slug ? 'active' : 'pending'}
          </Badge>
          {runningCount ? <Badge tone="info">{runningCount} in progress</Badge> : null}
          {blockedCount ? <Badge tone="danger">{blockedCount} blocked</Badge> : null}
          {waitingTask || health === 'waiting' ? <Badge tone="warning">waiting on you</Badge> : null}
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <Button asChild variant="secondary" size="sm">
            <Link
              to="/projects/$workspaceUuid/$projectUuid"
              params={{ workspaceUuid, projectUuid: project.uuid }}
            >
              <ClipboardCheck className="size-4" />
              Open cockpit
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link
              to="/projects/$workspaceUuid/$projectUuid/provisioning"
              params={{ workspaceUuid, projectUuid: project.uuid }}
              aria-label={`Open provisioning status for ${project.name}`}
            >
              <ServerCog className="size-4" />
              Provisioning status
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function RequestComposer({
  workspace,
  projects,
}: {
  workspace?: WorkspaceRead
  projects: BosunProjectRead[]
}) {
  const [message, setMessage] = React.useState('')
  const [title, setTitle] = React.useState('')
  const [projectUuid, setProjectUuid] = React.useState<string>('')
  const createRequest = useCreateBosunIntakeRequest(workspace?.uuid ?? '')
  const canSubmit = message.trim().length > 2
  const selectedProject = projects.find((project) => project.uuid === projectUuid)

  async function submitRequest(event: React.FormEvent) {
    event.preventDefault()
    if (!workspace || !canSubmit) return
    const body = message.trim()
    const requestTitle =
      title.trim() || body.split(/\r?\n/)[0]?.slice(0, 100) || 'Bosun request'
    await createRequest.mutateAsync({
      title: requestTitle,
      body,
      priority: 'normal',
      source: 'app',
      project_uuid: projectUuid || null,
      metadata: {
        surface: 'portfolio_cockpit',
        project_slug: selectedProject?.slug ?? null,
      },
      idempotency_key: `portfolio-${workspace.uuid}-${Date.now()}`,
    })
    setTitle('')
    setMessage('')
    setProjectUuid('')
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquarePlus className="size-4 text-primary" />
          Start a request
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pt-4">
        <form className="space-y-3" onSubmit={(event) => void submitRequest(event)}>
          <div className="space-y-2">
            <Label htmlFor="portfolio-request-title">Title</Label>
            <Input
              id="portfolio-request-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Launch copy, bug fix, release help..."
            />
          </div>
          <label className="space-y-2 text-sm font-medium">
            Project
            <select
              value={projectUuid}
              onChange={(event) => setProjectUuid(event.target.value)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
              disabled={!projects.length}
            >
              <option value="">Portfolio request</option>
              {projects.map((project) => (
                <option key={project.uuid} value={project.uuid}>{project.name}</option>
              ))}
            </select>
          </label>
        <Label htmlFor="portfolio-request">Post to Bosun intake</Label>
        <Textarea
          id="portfolio-request"
          value={message}
          onChange={(event) => {
            setMessage(event.target.value)
          }}
          placeholder="Describe the product change, bug, launch help, or follow-up you need..."
          className="min-h-24"
        />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {workspace
              ? `Routes under ${workspace.name}.`
              : 'Choose a workspace project after sign-in if routing needs more context.'}
          </p>
          <Button type="submit" size="sm" disabled={!workspace || !canSubmit || createRequest.isPending}>
            <MessageSquarePlus />
            {createRequest.isPending ? 'Submitting' : 'Submit request'}
          </Button>
        </div>
        </form>
        {createRequest.isSuccess ? (
          <Alert tone="success">
            <AlertDescription>
              Request received in the Bosun intake queue. Routing and task links will appear in request status.
            </AlertDescription>
          </Alert>
        ) : null}
        {createRequest.error ? (
          <Alert tone="danger">
            <AlertDescription>{formatError(createRequest.error)}</AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  )
}

function WaitingActionItem({
  item,
  project,
}: {
  item: BosunUserActionItemRead
  project?: BosunProjectRead
}) {
  return (
    <Link
      to="/projects/$workspaceUuid/$projectUuid"
      params={{ workspaceUuid: item.workspace_uuid, projectUuid: item.project_uuid }}
      className="block rounded-md border border-warning/30 bg-warning/10 p-3 text-sm hover:bg-warning/15"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium">{item.title}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {project?.name ?? 'Bosun project'} · {item.question ?? item.body ?? 'PM needs your input'}
          </p>
        </div>
        <Badge tone={item.status === 'open' ? 'warning' : 'info'}>{item.status}</Badge>
      </div>
    </Link>
  )
}

function RequestStatusCards({
  summaries,
}: {
  summaries: BosunCockpitProjectSummaryRead[]
}) {
  const requestSummaries = summaries.filter((summary) => summary.latest_intake_request)
  if (!requestSummaries.length) {
    return (
      <p className="text-sm text-muted-foreground">Open PM requests will appear here.</p>
    )
  }
  return (
    <div className="grid gap-3 lg:grid-cols-3">
      {requestSummaries.slice(0, 6).map((summary) => {
        const request = summary.latest_intake_request
        if (!request) return null
        return (
          <Link
            key={request.uuid}
            to="/projects/$workspaceUuid/$projectUuid"
            params={{ workspaceUuid: request.workspace_uuid, projectUuid: request.project_uuid ?? summary.project.uuid }}
            className="block rounded-md border border-white/10 bg-white/[0.035] p-3 text-sm hover:bg-white/[0.055]"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium">{request.title}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {summary.project.name} · {request.routed_task_id ?? request.routed_board_slug ?? 'routing pending'}
                </p>
              </div>
              <Badge tone={request.status === 'completed' ? 'success' : request.status === 'failed' ? 'danger' : 'info'}>
                {request.status}
              </Badge>
            </div>
          </Link>
        )
      })}
    </div>
  )
}

function WaitingInbox({
  actionItems,
  projects,
}: {
  actionItems: BosunUserActionItemRead[]
  projects: BosunProjectRead[]
}) {
  if (actionItems.length === 0) {
    return <p className="text-sm text-muted-foreground">No open action items.</p>
  }
  return (
    <>
      {actionItems.slice(0, 6).map((item) => (
        <WaitingActionItem
          key={item.uuid}
          item={item}
          project={projects.find((project) => project.uuid === item.project_uuid)}
        />
      ))}
    </>
  )
}

function PortfolioState({
  workspace,
  cockpit,
  actionItems,
}: {
  workspace?: WorkspaceRead
  cockpit?: BosunCockpitProjectSummaryRead[]
  actionItems: BosunUserActionItemRead[]
}) {
  const projects = useBosunProjects(workspace?.uuid)
  const workspaceProjects = projects.data ?? []
  return (
    <section id="start-request" className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_24rem]">
        <RequestComposer workspace={workspace} projects={workspaceProjects} />
        <Card tone="outline">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserRoundCheck className="size-4 text-warning" />
              Waiting on you
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 pt-4">
            <WaitingInbox actionItems={actionItems} projects={cockpit?.map((summary) => summary.project) ?? workspaceProjects} />
          </CardContent>
        </Card>
      </div>
      <Card tone="outline">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardCheck className="size-4 text-primary" />
            Request status
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <RequestStatusCards summaries={cockpit ?? []} />
        </CardContent>
      </Card>
    </section>
  )
}

function WorkspaceProjects({
  workspace,
  query,
  filter,
}: {
  workspace: WorkspaceRead
  query: string
  filter: ProjectFilter
}) {
  const { data: projects, isLoading, error } = useBosunProjects(workspace.uuid)
  const isArchived = workspace.archive_status === 'archived'
  const canProvision = canManageMembers(workspace.role) && !isArchived
  const visibleProjects =
    projects?.filter((project) => projectMatches(project, workspace, query, filter)) ?? []

  return (
    <section className="space-y-3">
      <SectionHeader
        title={workspace.name}
        description={`${workspace.member_count} member${workspace.member_count === 1 ? '' : 's'} · ${workspace.role}${isArchived ? ' · archived' : ''}`}
        actions={
          canProvision ? (
            <Button asChild size="sm">
              <Link to="/projects/$workspaceUuid/new" params={{ workspaceUuid: workspace.uuid }}>
                New project
                <ArrowRight />
              </Link>
            </Button>
          ) : undefined
        }
      />
      {isLoading ? (
        <div className="grid gap-3 md:grid-cols-2">
          <Skeleton className="h-44 w-full" />
          <Skeleton className="h-44 w-full" />
        </div>
      ) : error ? (
        <Alert tone="danger">
          <AlertDescription>{formatError(error)}</AlertDescription>
        </Alert>
      ) : projects && projects.length > 0 ? (
        visibleProjects.length > 0 ? (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {visibleProjects.map((project) => (
              <ProjectCard key={project.uuid} workspaceUuid={workspace.uuid} project={project} />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={Filter}
            variant="bare"
            title="No matching projects"
            description="Adjust the search or health filter to widen the portfolio view."
          />
        )
      ) : (
        <EmptyState
          icon={ServerCog}
          variant="bare"
          title="No Bosun projects"
          description={
            canProvision
              ? 'Provision the first Bosun project for this workspace.'
              : 'Ask a workspace admin to provision the first project.'
          }
          action={
            canProvision ? (
              <Button asChild size="sm">
                <Link to="/projects/$workspaceUuid/new" params={{ workspaceUuid: workspace.uuid }}>
                  Provision project
                </Link>
              </Button>
            ) : null
          }
        />
      )}
    </section>
  )
}

export function ProjectsIndexPage() {
  const { data: workspaces, isLoading, error } = useWorkspaces()
  const cockpit = useBosunCockpit()
  const portfolioActions = useBosunPortfolioActionItems()
  const [query, setQuery] = React.useState('')
  const [filter, setFilter] = React.useState<ProjectFilter>('all')
  const firstProvisionableWorkspace = workspaces?.find(
    (workspace) => canManageMembers(workspace.role) && workspace.archive_status !== 'archived',
  )

  return (
    <div className="mx-auto w-full max-w-7xl space-y-8 px-4 py-6 md:px-8 md:py-8">
      <section className="relative overflow-hidden rounded-lg border border-white/10 bg-white/[0.035] p-5 shadow-floating backdrop-blur-xl md:p-7">
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/70 to-transparent"
        />
        <PageHeader
          eyebrow="Fleet inventory"
          title="Project cockpit"
          description="Track launch, provisioning, Hermes startup, PM activity, kanban work, and items waiting on you across a 10+ project portfolio."
          actions={
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() =>
                  document.getElementById('start-request')?.scrollIntoView({ behavior: 'smooth' })
                }
              >
                <MessageSquarePlus />
                Start request
              </Button>
              <Button asChild>
                {firstProvisionableWorkspace ? (
                  <Link
                    to="/projects/$workspaceUuid/new"
                    params={{ workspaceUuid: firstProvisionableWorkspace.uuid }}
                  >
                    <ServerCog />
                    Provision project
                  </Link>
                ) : (
                  <Link to="/workspaces">
                    <ServerCog />
                    Provision project
                  </Link>
                )}
              </Button>
            </div>
          }
        />
        <div className="mt-4 flex flex-wrap gap-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/20 px-2.5 py-1">
            <SquareActivity className="size-3.5 text-primary" />
            Workspace-scoped operations
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/20 px-2.5 py-1">
            <UserRoundCheck className="size-3.5 text-warning" />
            Waiting-on-you inbox
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/20 px-2.5 py-1">
            <CheckCircle2 className="size-3.5 text-success" />
            Launch readiness at a glance
          </span>
        </div>
        <div className="mt-6 grid gap-3 lg:grid-cols-[minmax(16rem,1fr)_auto]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              aria-label="Search projects"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by project, workspace, health, assignee, profile, or recent update"
              className="pl-9"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto" aria-label="Project health filters">
            {FILTERS.map((item) => (
              <Button
                key={item.value}
                type="button"
                size="sm"
                variant={filter === item.value ? 'default' : 'secondary'}
                onClick={() => setFilter(item.value)}
              >
                {item.label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      <PortfolioState
        workspace={firstProvisionableWorkspace}
        cockpit={cockpit.data?.projects}
        actionItems={portfolioActions.data ?? []}
      />

      {isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : error ? (
        <Alert tone="danger">
          <AlertDescription>Failed to load workspaces: {formatError(error)}</AlertDescription>
        </Alert>
      ) : workspaces && workspaces.length > 0 ? (
        <div className="space-y-8">
          {workspaces.map((workspace) => (
            <WorkspaceProjects
              key={workspace.uuid}
              workspace={workspace}
              query={query}
              filter={filter}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={Building2}
          title="No workspaces yet"
          description="Create a workspace before provisioning a Bosun project."
          action={
            <Button asChild size="sm">
              <Link to="/workspaces">Open workspaces</Link>
            </Button>
          }
        />
      )}
    </div>
  )
}
